<template>
  <div id="hr-criteria-modal-parent">
    <Page title="人材基準管理">
      <template v-slot:action
        ><ActionButton
          title="判定"
          :color="mode === 'result' ? 'primary' : 'default'"
          shape="round"
          width="180"
          @click="mode = 'result'"/>
        <ActionButton
          class="uk-margin-left"
          title="基準管理"
          :color="mode === 'manage' ? 'primary' : 'default'"
          shape="round"
          width="180"
          @click="mode = 'manage'"
      /></template>
      <template v-slot:content>
        <div v-show="mode === 'manage'">
          <div class="table-action">
            <div>
              <div class="search-field">
                <SearchField
                  @search="onSearch"
                  :count="criteria && criteria.length"
                  :tableData="criteria"
                  :filterColumns="filterColumns"
                  :key="renewKey"
                />
              </div>
              <ActionButton title="新規追加" color="default" icon="plus" @click="onAddCriteria" />
            </div>
          </div>
          <div class="uk-card uk-card-default uk-card-body">
            <Table :header="header" :tableData="criteria" @column-click="onColumnClick" />
          </div>
          <div
            class="uk-flex-top position-form-modal"
            uk-modal
            ref="modalRef"
            container="#hr-criteria-modal-parent"
            :bg-close="false"
          >
            <HrCriterionForm :key="formKey" @submitted="onSubmitted" :criterion="criterion" />
          </div>
        </div>
        <div v-show="mode === 'result'" class="uk-card uk-card-default uk-card-body">
          <div class="content-header">
            <ActionButton
              title="分析対象追加"
              color="primary"
              width="150"
              @click="onAddButtonClick"
              :uk-tooltip="`title: 選択可能人数は${participantsMin}人〜${participantsMax}人です。; pos: top-left;`"
            />
          </div>
          <div class="content-main">
            <div class="selected-users">
              <UserSelector :users="selectedUsers" :readOnly="true" :selectable="true" @user-click="switchUser" />
            </div>
            <div class="uk-margin-left criteria-list">
              <FieldLabel title="人材基準の選択" />
              <HelpIcon
                tooltipText="分析対象者と基準を選択してダウンロードボタンをクリックしてください"
                position="top-left"
                cls="uk-active uk-width-auto"
              />
              <div class="uk-margin-small-left uk-margin-top">
                <p v-for="(c, index) in criteria" :key="index">
                  <input type="checkbox" :id="`criteria-${c.id}`" :value="c.id" v-model="selectedCriteriaIds" />
                  <label :for="`criteria-${c.id}`">{{ c.name }}</label>
                </p>
              </div>
              <ActionButton
                title="ダウンロード"
                :color="'primary'"
                shape="round"
                width="180"
                @click="onDownload"
                :disabled="disableDownload"
              />
            </div>
          </div>
          <div class="uk-flex-top" uk-modal ref="selectionModal" container="#hr-criteria-modal-parent" :bg-close="false">
            <div class="uk-modal-dialog uk-margin-auto-vertical">
              <div class="uk-modal-body">
                <DepartmentUserSelector
                  :key="departmentUserSelectorFormKey"
                  :full="true"
                  :initialSelectedUsers="selectedUsers"
                  :participantsMax="participantsMax"
                  :participantsMin="participantsMin"
                  @user-selection-change="onTargetUserSelectionChange"
                  @on-selection-done-button-click="userSelect"
                  @close="onCloseButtonClick"
                  v-on:keydown.esc="onCloseButtonClick"
                />
              </div>
            </div>
          </div>
        </div>
      </template>
    </Page>
  </div>
</template>
<style lang="scss" scoped>
@include table-action;
@include modal-form;
.search-field {
  margin-right: $spacing-medium;
}
.uk-card-body {
  padding: 30px;
  .content-main {
    display: flex;
    .criteria-list {
      p {
        line-height: 0.1em;
      }
    }
  }
  .content-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: $spacing-ex-small;
  }
}
.selected-users {
  width: 250px;
  min-width: 250px;
  margin-right: $spacing-medium;
  max-height: 500px;
  min-height: 300px;
  .user-selector-container {
    height: 100%;
  }
}
.uk-modal-dialog {
  width: unset;
  min-width: 730px;
}
</style>
<script lang="ts">
import { Options } from "vue-class-component";
import { Ref } from "vue-property-decorator";
import { ClientApi, GetHrCriterion, StatsApi } from "../../autogen";
import { SelectableProfile, GetProfile } from "../../types";
import Page from "../molecules/Page.vue";
import ActionButton from "../atoms/ActionButton.vue";
import Table from "../molecules/Table.vue";
import UIkit from "uikit";
import HrCriterionForm from "../organisms/HrCriterionForm.vue";
import SearchField from "../atoms/SearchField.vue";
import ChartViewMixin from "../../helpers/mixins/chart-view";
import UserSelector from "../molecules/UserSelector.vue";
import DepartmentUserSelector from "../organisms/DepartmentUserSelector.vue";
import FieldLabel from "../atoms/FieldLabel.vue";
import HelpIcon from "../atoms/HelpIcon.vue";
import Store from "../../store";

@Options({
  components: {
    Page,
    ActionButton,
    Table,
    HrCriterionForm,
    SearchField,
    UserSelector,
    DepartmentUserSelector,
    FieldLabel,
    HelpIcon
  }
})
export default class HrCriteria extends ChartViewMixin {
  private readonly storeInstance = Store.getInstance();
  mode: "result" | "manage" = "manage";
  participantsMin = 1;
  participantsMax = 100;
  criteria: GetHrCriterion[] = [];
  criterion: GetHrCriterion | null = null;
  selectedUsers: SelectableProfile[] = [];
  selectedCriteriaIds: string[] = [];
  departmentUserSelectorFormKey = 0;
  formKey = 0;
  header = [
    { title: "人材基準名", sortable: true, key: "name", width: "expand" },
    { title: "編集", type: "button", key: "edit", width: 150 }
  ];
  filterColumns = ["name"];
  modalRef!: HTMLElement;
  query = "";
  renewKey = 0;

  private async loadData() {
    const response = await new ClientApi().getHrCriteria({});
    this.criteria = response.data;
    this.renewKey = Date.now();
  }
  mounted() {
    this.$nextTick(() => {
      this.modalRef = this.$refs.modalRef as HTMLElement;
    });
  }

  async beforeMount() {
    await this.loadData();
  }
  beforeUnmount() {
    if (this.modalRef) this.modalRef.remove();
  }
  get disableDownload(): boolean {
    return this.selectedUsers.length === 0 || this.selectedCriteriaIds.length === 0;
  }
  onAddCriteria() {
    this.formKey = Date.now();
    this.criterion = null;
    UIkit.modal(this.modalRef).show();
  }
  async onSubmitted() {
    UIkit.modal(this.modalRef).hide();
    await this.loadData();
  }
  async onSearch(searchResult: [] | null) {
    if (searchResult) {
      this.criteria = searchResult;
    } else {
      await this.loadData();
    }
  }
  onColumnClick(location: { row: number; col: string }) {
    if (location.col === "edit") {
      this.criterion = this.criteria[location.row];
      this.formKey = Date.now();
      UIkit.modal(this.modalRef).show();
    }
  }
  userSelect() {
    this.onSelectionDoneButtonClick();
  }
  async onDownload() {
    Store.getInstance().setIsLoading(true);
    const response = await new StatsApi().getCriteriaJudgmentResults(
      {
        peopleIds: this.selectedUsers.map(user => user.id).join(","),
        criteriaList: this.selectedCriteriaIds.join(",")
      },
      { responseType: "arraybuffer" }
    );
    const url = window.URL.createObjectURL(
      new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
    );
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `人材基準判定_${new Date().toISOString()}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();

    Store.getInstance().setIsLoading(false);
  }
}
</script>
